<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useSiteDraft } from '@/composables/useSiteDraft'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'
import { siteAddress } from '@/Utils/siteUrl'
import FormButton from '@/components/Atoms/FormButton.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconMonitorOutline from '@/components/icon/IconMonitorOutline.vue'
import IconMobile from '@/components/icon/IconMobile.vue'
import IconRepeat from '@/components/icon/IconRepeat.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'

// Live preview of the site with its unpublished changes: an <iframe> of the
// castr preview link (POST /sites/{id}/preview, ≤ 1 h). After every draft
// save the parent bumps `version`; the pane asks the runtime to reload
// (`postMessage('castr:reload')`) and falls back to reloading the frame.
//
// The frame renders the site at a real device width (desktop 1280 px, phone
// 390 px) and is scaled to the stage, so a narrow pane still shows the actual
// desktop layout instead of the site's own narrow breakpoint. The stage keeps
// 16:9 and follows the pane width.
//
// The preview is served on the site's own address; while the edge is still
// routing a new one (castr `hostReadiness.ready === false`) the frame would
// only show a 530 page, so the pane waits and loads once the address serves.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		default: null,
	},
	/** Increment to reload the frame (draft saved / published / discarded). */
	version: {
		type: Number,
		default: 0,
	},
	/** Path inside the site to preview (e.g. `/` or `/calendar`). */
	path: {
		type: String,
		default: '/',
	},
	/** Cap on the stage height in px (0 = none); the stage otherwise follows the 16:9 width. */
	maxHeight: {
		type: Number,
		default: 0,
	},
})

/** Logical frame size per device; the stage scales it. */
const DEVICES = {
	desktop: { width: 1280, height: 720, label: 'Desktop' },
	mobile: { width: 390, height: 844, label: 'Mobile' },
}

const draft = useSiteDraft(() => props.siteId)
const readiness = useSiteHostReadiness(() => props.siteId)
const waitingForHost = computed(() => readiness.gated.value)
const frame = ref(null)
const stage = ref(null)
const stageWidth = ref(0)
const device = ref('desktop') // desktop | mobile
const preview = ref(null)
const unavailable = ref(false)
const loadingFrame = ref(false)
const src = ref('')
let fallbackTimer = null
let resizeObserver = null

const previewUrl = (p) => {
	if (!p?.url) return ''
	try {
		const u = new URL(p.url)
		if (props.path && props.path !== '/') u.pathname = props.path
		return u.toString()
	} catch {
		return p.url
	}
}
const expiresSoon = () => !preview.value || new Date(preview.value.expires_at).getTime() - Date.now() < 120_000

async function mint({ force = false } = {}) {
	const p = await draft.getPreview({ force })
	if (!p) {
		unavailable.value = true
		preview.value = null
		return null
	}
	unavailable.value = false
	preview.value = p
	return p
}

async function loadFrame({ force = false } = {}) {
	if (waitingForHost.value) return
	const p = force || expiresSoon() ? await mint({ force }) : preview.value
	if (!p) return
	loadingFrame.value = true
	// changing `src` (even to the same URL with a cache-busting hash) reloads the document
	const url = previewUrl(p)
	src.value = `${url}${url.includes('#') ? '' : '#'}v${Date.now()}`
}

function reload() {
	clearTimeout(fallbackTimer)
	const win = frame.value?.contentWindow
	if (win && src.value) {
		try {
			win.postMessage('castr:reload', '*')
		} catch {
			// cross-origin frame without a window yet
		}
	}
	// The runtime only listens in preview mode on newer builds; reload regardless.
	fallbackTimer = setTimeout(() => loadFrame(), 900)
}

const onFrameLoad = () => { loadingFrame.value = false }
const openTab = () => { if (!waitingForHost.value) draft.openPreview() }

watch(() => props.version, () => { if (!unavailable.value) reload() })
watch(() => props.path, () => { if (!unavailable.value) loadFrame() })
// the address just started serving: load the frame now
watch(waitingForHost, (waiting, before) => { if (before && !waiting) loadFrame({ force: expiresSoon() }) })

onMounted(() => {
	loadFrame({ force: false })
	if (typeof ResizeObserver !== 'undefined' && stage.value) {
		resizeObserver = new ResizeObserver((entries) => { stageWidth.value = entries[0]?.contentRect?.width || 0 })
		resizeObserver.observe(stage.value)
	} else if (stage.value) {
		stageWidth.value = stage.value.getBoundingClientRect().width
	}
})
onBeforeUnmount(() => {
	clearTimeout(fallbackTimer)
	resizeObserver?.disconnect()
})

// The stage follows the pane width in the device's aspect (16:9 desktop, phone
// portrait for mobile) and the frame is scaled into it. A full-width phone
// would be taller than the viewport, so the mobile stage is capped.
const MOBILE_STAGE_MAX = 560
const stageHeight = computed(() => {
	const d = DEVICES[device.value]
	const natural = Math.round((stageWidth.value * d.height) / d.width)
	const cap = device.value === 'mobile' ? Math.min(props.maxHeight || MOBILE_STAGE_MAX, MOBILE_STAGE_MAX) : props.maxHeight
	return cap ? Math.min(natural, cap) : natural
})
const frameScale = computed(() => {
	const d = DEVICES[device.value]
	if (!stageWidth.value || !stageHeight.value) return 1
	return Math.min(stageWidth.value / d.width, stageHeight.value / d.height)
})
const frameStyle = computed(() => {
	const d = DEVICES[device.value]
	return {
		width: `${d.width}px`,
		height: `${d.height}px`,
		transform: `scale(${frameScale.value})`,
	}
})
const scaledFrameWidth = computed(() => Math.round(DEVICES[device.value].width * frameScale.value))
const expiresLabel = computed(() => (preview.value?.expires_at
	? new Date(preview.value.expires_at).toLocaleTimeString(undefined, { timeStyle: 'short' })
	: ''))
const siteUrl = computed(() => siteAddress(props.site))
const openLive = () => { if (siteUrl.value) window.open(siteUrl.value, '_blank', 'noopener') }
</script>

<template>
	<aside class="flex min-w-0 flex-col rounded-lg bg-surface-2 p-3" data-testid="site-preview-pane" aria-label="Live preview">
		<div class="mb-2 flex items-center gap-2">
			<p class="mb-0 mr-auto truncate text-tiny font-medium text-surface-8">Live preview</p>
			<div class="flex items-center gap-0.5 rounded-md bg-surface-3 p-0.5" role="radiogroup" aria-label="Preview device" data-testid="preview-device">
				<Tooltip v-for="(d, key) in DEVICES" :key="key" :content="`${d.label} (${d.width} px)`">
					<button type="button" role="radio" class="grid size-7 place-items-center rounded transition-colors" :class="device === key ? 'bg-surface-5 text-white' : 'text-surface-7 hover:text-white'"
						:aria-checked="device === key" :aria-label="d.label" :data-testid="`preview-device-${key}`" @click="device = key">
						<icon-base :width="16" :height="16" fill="none"><icon-monitor-outline v-if="key === 'desktop'" /><icon-mobile v-else /></icon-base>
					</button>
				</Tooltip>
			</div>
			<Tooltip content="Reload preview" wrap-trigger>
				<FormButton type="secondary" size="sm" is-icon aria-label="Reload preview" :disabled="unavailable || waitingForHost" data-testid="preview-reload" @click="loadFrame({ force: expiresSoon() })">
					<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-repeat /></icon-base>
				</FormButton>
			</Tooltip>
			<Tooltip :content="waitingForHost ? readiness.lockedTitle : 'Open preview in a new tab'" wrap-trigger>
				<FormButton type="secondary" size="sm" is-icon aria-label="Open preview in a new tab" :disabled="waitingForHost" data-testid="preview-open" @click="openTab">
					<icon-base :width="16" :height="16" fill="none"><icon-link-external /></icon-base>
				</FormButton>
			</Tooltip>
		</div>

		<div ref="stage" class="relative flex w-full justify-center overflow-hidden rounded-md bg-surface-1 ring-1 ring-surface-3" :style="{ height: `${stageHeight || 240}px` }"
			data-testid="preview-stage" :data-device="device">
			<div v-if="waitingForHost" class="grid size-full place-items-center p-6 text-center" data-testid="preview-waiting-host" :data-phase="readiness.phase.value">
				<div class="max-w-xs">
					<Spinner v-if="!readiness.failed.value" classes="mb-2 justify-center" spinner-size="18px" spinner-color="var(--c-main-light)" />
					<p class="mb-1 text-sm font-medium text-white">{{ readiness.failed.value ? 'Your site address is not ready' : 'Setting up your site address…' }}</p>
					<p class="mb-0 text-tiny text-surface-7">
						<template v-if="readiness.failed.value">{{ readiness.detail.value || 'The address could not be set up.' }} The preview loads once it is reachable.</template>
						<template v-else>{{ readiness.phaseLabel.value }} — this usually takes 1–2 minutes. The preview loads here by itself once {{ readiness.host.value }} is reachable.</template>
					</p>
				</div>
			</div>
			<div v-else-if="unavailable" class="grid size-full place-items-center p-6 text-center" data-testid="preview-unavailable">
				<div class="max-w-xs">
					<p class="mb-1 text-sm font-medium text-white">Preview isn't available here yet</p>
					<p class="mb-3 text-tiny text-surface-7">The server does not issue preview links on this environment. Save your changes and publish to see them on the site.</p>
					<FormButton v-if="siteUrl && site?.status === 'active'" type="secondary" size="sm" @click="openLive">Open the live site</FormButton>
				</div>
			</div>
			<template v-else>
				<!-- The scaled frame keeps its layout size in the flow, so a sized shell holds its visual footprint. -->
				<div v-if="src" class="relative shrink-0 overflow-hidden" :class="device === 'mobile' ? 'rounded-b-xl' : ''" :style="{ width: `${scaledFrameWidth}px`, height: `${stageHeight}px` }">
					<iframe ref="frame" :src="src" title="Site preview" class="absolute left-0 top-0 origin-top-left border-0 bg-white" :style="frameStyle"
						sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerpolicy="no-referrer" data-testid="preview-frame" @load="onFrameLoad" />
				</div>
				<div v-if="loadingFrame || !src" class="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden" aria-hidden="true">
					<div class="h-full w-1/3 animate-pulse bg-main"></div>
				</div>
			</template>
		</div>
		<p class="mb-0 mt-2 text-[11px] leading-snug text-surface-7">Shows your unpublished changes as viewers would see them. Links open inside the preview.</p>
		<p v-if="expiresLabel" class="mb-0 mt-0.5 text-[11px] text-surface-6" data-testid="preview-expires">Link valid until {{ expiresLabel }}.</p>
	</aside>
</template>
